import { supabase } from '../lib/supabaseClient';
import {
  buildValidationInsert,
  eligibleSnapshots,
  evidenceMetrics,
  finiteMetric,
  isOnOrAfterImplementation,
  isResultDecision,
  readEvidenceContext,
  rejectionNoteIsMissing,
  type EvidenceContext,
  type KaizenResultSource,
  type ResultDecision,
  type SnapshotResultSource,
} from '../utils/kaizenResultValidation';

export interface KaizenResultValidation {
  id: string;
  organization_id: string;
  kaizen_id: string;
  snapshot_id: string;
  decision: ResultDecision;
  note: string | null;
  evidence_context: EvidenceContext | null;
  created_by: string;
  created_at: string;
  validator_name: string | null;
}

export interface CreateKaizenResultValidationInput {
  kaizenId: string;
  snapshotId: string;
  decision: ResultDecision;
  note?: string | null;
}

function requireClient() {
  if (!supabase) throw new Error('Não foi possível registrar a validação.');
  return supabase;
}

function asSource(row: Record<string, unknown>): KaizenResultSource {
  return {
    id: String(row.id),
    organization_id: row.organization_id == null ? null : String(row.organization_id),
    implementation_date: row.implementation_date == null ? null : String(row.implementation_date),
    baseline_lead_time: finiteMetric(row.baseline_lead_time),
    target_lead_time: finiteMetric(row.target_lead_time),
    baseline_cycle_time: finiteMetric(row.baseline_cycle_time),
    target_cycle_time: finiteMetric(row.target_cycle_time),
    baseline_ftr: finiteMetric(row.baseline_ftr),
    target_ftr: finiteMetric(row.target_ftr),
    baseline_capacity_fte: finiteMetric(row.baseline_capacity_fte),
    target_capacity_fte: finiteMetric(row.target_capacity_fte),
  };
}

function asSnapshot(row: Record<string, unknown>): SnapshotResultSource {
  return {
    id: String(row.id),
    kaizen_id: String(row.kaizen_id),
    measured_at: String(row.measured_at),
    created_by: row.created_by == null ? null : String(row.created_by),
    lead_time: finiteMetric(row.lead_time),
    cycle_time: finiteMetric(row.cycle_time),
    ftr: finiteMetric(row.ftr),
    capacity_fte: finiteMetric(row.capacity_fte),
  };
}

async function sessionUserId(): Promise<string> {
  const { data: { session } } = await requireClient().auth.getSession();
  const id = session?.user?.id;
  if (!id) throw new Error('Entre na sua conta para validar o resultado.');
  return id;
}

async function validatorNames(userIds: string[]): Promise<Record<string, string | null>> {
  if (userIds.length === 0) return {};
  const { data } = await requireClient()
    .from('user_profiles')
    .select('user_id, name')
    .in('user_id', userIds);
  return Object.fromEntries(
    (data ?? []).map((profile: { user_id: string; name?: string | null }) => [profile.user_id, profile.name ?? null]),
  );
}

function mapValidation(row: Record<string, unknown>, names: Record<string, string | null>): KaizenResultValidation | null {
  const decision = row.decision === 'validated' || row.decision === 'rejected' ? row.decision : null;
  if (!decision || row.created_by == null) return null;
  const createdBy = String(row.created_by);
  return {
    id: String(row.id),
    organization_id: String(row.organization_id),
    kaizen_id: String(row.kaizen_id),
    snapshot_id: String(row.snapshot_id),
    decision,
    note: row.note == null ? null : String(row.note),
    evidence_context: readEvidenceContext(row.evidence_context),
    created_by: createdBy,
    created_at: String(row.created_at),
    validator_name: names[createdBy] ?? null,
  };
}

export class KaizenResultValidationService {
  static async loadKaizen(kaizenId: string): Promise<KaizenResultSource> {
    const { data, error } = await requireClient()
      .from('kaizen_records')
      .select('*')
      .eq('id', kaizenId)
      .maybeSingle();
    if (error || !data) {
      console.error('[KaizenResultValidationService.loadKaizen]', error?.message);
      throw new Error('Não foi possível carregar o Kaizen.');
    }
    return asSource(data as Record<string, unknown>);
  }

  static async loadSnapshots(kaizenId: string): Promise<SnapshotResultSource[]> {
    const { data, error } = await requireClient()
      .from('metrics_snapshots')
      .select('*')
      .eq('kaizen_id', kaizenId);
    if (error) {
      console.error('[KaizenResultValidationService.loadSnapshots]', error.message);
      throw new Error('Não foi possível carregar as medições.');
    }
    return ((data ?? []) as Record<string, unknown>[]).map(asSnapshot);
  }

  static async listEligibleSnapshots(kaizenId: string): Promise<SnapshotResultSource[]> {
    const kaizen = await this.loadKaizen(kaizenId);
    const snapshots = await this.loadSnapshots(kaizenId);
    return eligibleSnapshots(kaizen, snapshots);
  }

  static async listByKaizen(kaizenId: string): Promise<KaizenResultValidation[]> {
    const { data, error } = await requireClient()
      .from('kaizen_result_validations')
      .select('*')
      .eq('kaizen_id', kaizenId)
      .order('created_at', { ascending: false });
    if (error) {
      console.error('[KaizenResultValidationService.listByKaizen]', error.message);
      const missingTable = error.code === '42P01'
        || error.code === 'PGRST205'
        || /kaizen_result_validations/i.test(error.message ?? '');
      const failure = new Error(
        missingTable
          ? 'A validação de resultado ainda não está disponível neste ambiente.'
          : 'Não foi possível carregar as validações.',
      );
      failure.name = missingTable ? 'KaizenResultValidationUnavailable' : 'Error';
      throw failure;
    }
    const rows = (data ?? []) as Record<string, unknown>[];
    const names = await validatorNames([...new Set(rows.map((row) => String(row.created_by ?? '')).filter(Boolean))]);
    return rows
      .map((row) => mapValidation(row, names))
      .filter((row): row is KaizenResultValidation => row != null);
  }

  static async getLatestByKaizen(kaizenId: string): Promise<KaizenResultValidation | null> {
    const [latest] = await this.listByKaizen(kaizenId);
    return latest ?? null;
  }

  static async createValidation(input: CreateKaizenResultValidationInput): Promise<KaizenResultValidation> {
    if (!isResultDecision(input.decision)) {
      throw new Error('Não foi possível registrar a validação.');
    }
    if (rejectionNoteIsMissing(input.decision, input.note)) {
      throw new Error('Informe a observação para rejeitar.');
    }
    const userId = await sessionUserId();
    const kaizen = await this.loadKaizen(input.kaizenId);
    const snapshots = await this.loadSnapshots(input.kaizenId);
    const snapshot = snapshots.find((item) => item.id === input.snapshotId);
    if (!snapshot || snapshot.kaizen_id !== kaizen.id) {
      throw new Error('Esta medição não pertence ao Kaizen.');
    }
    if (kaizen.implementation_date == null) {
      throw new Error('Informe a data de implementação antes de validar o resultado.');
    }
    if (!isOnOrAfterImplementation(kaizen.implementation_date, snapshot.measured_at)) {
      throw new Error('Registre uma medição após a implementação para validar o resultado.');
    }
    if (evidenceMetrics(kaizen, snapshot).length === 0) {
      throw new Error('Não há baseline comparável nesta medição.');
    }
    const payload = buildValidationInsert({
      kaizen,
      snapshot,
      decision: input.decision,
      note: input.note,
      sessionUserId: userId,
    });
    const { data, error } = await requireClient()
      .from('kaizen_result_validations')
      .insert(payload)
      .select('*')
      .single();
    if (error || !data) {
      console.error('[KaizenResultValidationService.createValidation]', error?.message);
      throw new Error('Não foi possível registrar a validação.');
    }
    const names = await validatorNames([userId]);
    const mapped = mapValidation(data as Record<string, unknown>, names);
    if (!mapped) throw new Error('Não foi possível registrar a validação.');
    return mapped;
  }
}
